import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Fab,
  Collapse,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  HelpOutline as HelpIcon,
  Close as CloseIcon,
  Email as EmailIcon,
} from '@mui/icons-material';
import { useTenantConfig } from '../../contexts/TenantConfigContext';

const HelpWidget: React.FC = () => {
  const [isExpanded, setIsExpanded] = useState(false);
  const { config } = useTenantConfig();

  // Default support email and name
  const defaultEmail = 'lloyd@solicitors.co.za';
  const defaultName = 'Lloyd Manning';

  // Use account manager info if available, otherwise use defaults
  const supportEmail = config?.accountManager?.email || defaultEmail;
  const supportName = config?.accountManager?.name || defaultName;
  const organizationName = config?.branding?.displayName || config?.organizationId || 'Organization';

  // Create mailto link with pre-filled subject
  const subject = encodeURIComponent(`Support Request - ${organizationName}`);
  const mailtoLink = `mailto:${supportEmail}?subject=${subject}`;

  const handleToggle = () => {
    setIsExpanded(!isExpanded);
  };

  const handleSendEmail = () => {
    window.location.href = mailtoLink;
    setIsExpanded(false);
  };

  return (
    <Box
      sx={{
        position: 'fixed',
        // Clears the phone bottom navigation (56px) below the sm breakpoint.
        bottom: { xs: 80, sm: 24 },
        right: 24,
        zIndex: 1300,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-end',
        gap: 1.5,
        pointerEvents: 'none',
        '& > *': {
          pointerEvents: 'auto',
        },
      }}
    >
      {/* Expandable Card */}
      <Collapse in={isExpanded}>
        <Card
          sx={{
            width: 300,
            borderRadius: '14px',
            boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
            border: '1px solid',
            borderColor: 'divider',
            overflow: 'hidden',
            mb: 0.5,
            '@media (max-width: 600px)': {
              width: 270,
            },
          }}
        >
          <Box
            sx={{
              background: 'linear-gradient(135deg, #223343, #1b2d43)',
              p: 2,
              pb: 1.5,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-start',
            }}
          >
            <Typography sx={{ fontWeight: 700, fontSize: '0.95rem', color: 'white' }}>
              Need Help?
            </Typography>
            <IconButton size="small" onClick={handleToggle} sx={{ mt: -0.5, mr: -0.5, color: 'rgba(255,255,255,0.5)', '&:hover': { color: 'white' } }}>
              <CloseIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Box>
          <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
            <Typography sx={{ fontSize: '0.82rem', color: 'text.secondary', mb: 2 }}>
              Contact your account manager for assistance.
            </Typography>

            <Box sx={{ mb: 1.5 }}>
              <Typography sx={{ fontSize: '0.68rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'text.secondary', mb: 0.25 }}>
                Contact
              </Typography>
              <Typography sx={{ fontSize: '0.85rem', fontWeight: 600 }}>
                {supportName}
              </Typography>
            </Box>

            <Box sx={{ mb: 2 }}>
              <Typography sx={{ fontSize: '0.68rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'text.secondary', mb: 0.25 }}>
                Email
              </Typography>
              <Typography sx={{ fontSize: '0.85rem', fontWeight: 500, wordBreak: 'break-word' }}>
                {supportEmail}
              </Typography>
            </Box>

            <Button
              variant="contained"
              fullWidth
              startIcon={<EmailIcon sx={{ fontSize: 16 }} />}
              onClick={handleSendEmail}
              sx={{
                textTransform: 'none',
                fontWeight: 600,
                fontSize: '0.85rem',
                bgcolor: '#223343',
                borderRadius: '6px',
                py: 0.8,
                '&:hover': {
                  bgcolor: '#1b2d43',
                },
              }}
            >
              Send Email
            </Button>
          </CardContent>
        </Card>
      </Collapse>

      {/* Floating Action Button */}
      <Tooltip 
        title={isExpanded ? "Close Help" : "Need Help? Contact Support"} 
        placement="left" 
        arrow
      >
        <Fab
          aria-label="help"
          onClick={handleToggle}
          sx={{
            width: 48,
            height: 48,
            minHeight: 'unset',
            bgcolor: '#223343',
            color: 'white',
            boxShadow: '0 2px 12px rgba(0,0,0,0.15)',
            '&:hover': {
              bgcolor: '#1b2d43',
              boxShadow: '0 4px 20px rgba(10,192,247,0.15)',
              color: '#0ac0f7',
            },
            transition: 'all 0.2s ease',
            '@media (max-width: 600px)': {
              width: 44,
              height: 44,
            },
          }}
        >
          {isExpanded ? <CloseIcon sx={{ fontSize: 22 }} /> : <HelpIcon sx={{ fontSize: 22 }} />}
        </Fab>
      </Tooltip>
    </Box>
  );
};

export default HelpWidget;
